import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import BsModal from './BsModal';
import { showSuccess, showError } from '../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../hooks/useCurrentModule';
import { BSs } from "../../../types/BodyShape";
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


const BodyShape: React.FC = () => {
  const [bodyshapetypedata, setBodyshapetypedata] = useState<BSs[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<BSs | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchBs = async () => {
    try {
      const data = await apiRequest<BSs[]>({
        method: 'get',
        url: 'body-shape',
      });
      setBodyshapetypedata(data);
    } catch (error) {
      showError('Failed to fetch body shape ');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchBs();
  }, []);

  const handleEditClick = (record: BSs) => {
    setIsEditMode(true);
    setInitialValues({
      bs_id: record.bs_id,
      bs_type: record.bs_type,
      bs_status: record.bs_status,
      bs_name: record.bs_name,
      bs_image: record.bs_image,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchBs();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (bs_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-body-shape/${bs_id}`,
      });
      showSuccess('Body Shape  deleted');
      fetchBs();
    } catch {
      showError('Failed to delete body shape ');
    }
  };

  const handleStatusToggle = async (bsId: number) => {
    setBodyshapetypedata((prev) =>
      prev.map((item) =>
        item.bs_id === bsId
          ? { ...item, bs_status: item.bs_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-body-shape/${bsId}`,
      });
      showSuccess('Body Shape  status updated');
    } catch (error) {
      showError('Failed to update status');
      setBodyshapetypedata((prev) =>
        prev.map((item) =>
          item.bs_id === bsId
            ? { ...item, bs_status: item.bs_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<BSs>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<BSs> = [
    {
      title: 'Body Shape Name',
      dataIndex: 'bs_name',
      key: 'bs_name',
      width: 200,
      filters: Array.from(new Set(bodyshapetypedata.map((s) => s.bs_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.bs_name || null,
      // onFilter: (value, record) => record.bs_name.includes(value as string),
      // sorter: (a, b) => a.bs_name.localeCompare(b.bs_name),
      sortOrder: sortedInfo.columnKey === 'bs_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.bs_name}</div>,
    },
    {
      title: 'Body Shape type',
      dataIndex: 'bs_type',
      key: 'bs_type',
      width: 500,
      filters: Array.from(new Set(bodyshapetypedata.map((s) => s.bs_type))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.bs_type || null,
      onFilter: (value, record) => record.bs_type.includes(value as string),
      sorter: (a, b) => a.bs_type.localeCompare(b.bs_type),
      sortOrder: sortedInfo.columnKey === 'bs_type' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.bs_type}</div>,
    },
    {
      title: 'Image',
      dataIndex: 'bs_image',
      key: 'bs_image',
      width: 500,
      render: (_: any, record: any) =>
        record ? (
          <img
            src={record.bs_image}
            alt="Body Shape "
            style={{
              width: 40,
              height: 50,
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />
        ) : (
          '-'
        ),

    }
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: BSs) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.bs_status}
                onChange={() => handleStatusToggle(record.bs_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this bs?"
              onConfirm={() => handleDelete(record.bs_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
           )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Body Shape </Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Body Shape 
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<BSs>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="bs_id"
          dataSource={bodyshapetypedata.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No Body Shape  found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, bodyshapetypedata.length)} of{' '}
                {bodyshapetypedata.length}
              </span>
              <Pagination
                {...pagination}
                total={bodyshapetypedata.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <BsModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        bodyshapetypedata={bodyshapetypedata}
      />
    </>
  );
};

export default BodyShape;